import * as React from 'react';
import Head from 'next/head';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useAtomValue, useSetAtom } from 'jotai';

import { useAuth } from '@restworld/feature-authentication';
import { useOrganizationsService } from '@restworld/data-services';
import {
  orgOnboardingExistingUserFlowAtom,
  orgOnboardingReferralInfoAtom,
  orgOnboardingReferralPrefillAtom,
  orgOnboardingPartnerInfoAtom,
  orgOnboardingRestaurantDataAtom,
  orgOnboardingJPDataAtom,
  OrgOnboardingReferralInfo,
} from './use-organization-onboarding';
import { InvalidReferralCode } from './invalid-referral-code';

const OrganizationOnboarding = dynamic(() => import('./onboarding'), {
  ssr: false,
});

export interface GettingStartedOgTags {
  title: string;
  description: string;
  image: string;
  canonicalUrl: string;
}

export interface GettingStartedPageProps {
  og: GettingStartedOgTags;
}

export const GettingStartedPage: React.FunctionComponent<GettingStartedPageProps> = ({ og }) => {
  const router = useRouter();
  const { isAuthenticated, isInitialized } = useAuth();
  const isExistingUserFlow = useAtomValue(orgOnboardingExistingUserFlowAtom);
  const setExistingUserFlow = useSetAtom(orgOnboardingExistingUserFlowAtom);
  const setReferralInfo = useSetAtom(orgOnboardingReferralInfoAtom);
  const setReferralPrefill = useSetAtom(orgOnboardingReferralPrefillAtom);
  const setPartnerInfo = useSetAtom(orgOnboardingPartnerInfoAtom);
  const restaurantData = useAtomValue(orgOnboardingRestaurantDataAtom);
  const jpData = useAtomValue(orgOnboardingJPDataAtom);
  const orgService = useOrganizationsService();

  const [isReferralLoading, setIsReferralLoading] = React.useState(true);
  const [isInvalidReferralCode, setIsInvalidReferralCode] = React.useState(false);
  const [isPartnerLoading, setIsPartnerLoading] = React.useState(true);

  // Track whether user was authenticated when page first loaded.
  // This prevents redirecting users who become authenticated during the onboarding flow.
  const wasAuthenticatedOnMount = React.useRef<boolean | null>(null);

  React.useEffect(() => {
    if (isInitialized && wasAuthenticatedOnMount.current === null) {
      wasAuthenticatedOnMount.current = isAuthenticated;
    }
  }, [isAuthenticated, isInitialized]);

  // One-shot mount tasks: restore referral info saved across a Start-Fresh
  // reload. Step reconciliation lives in useOrgOnboardingUrlSync
  // (see onboarding.tsx) to avoid a mount-order race with that hook.
  const didMountTasksRef = React.useRef(false);
  React.useEffect(() => {
    if (didMountTasksRef.current) return;
    didMountTasksRef.current = true;
    if (isExistingUserFlow) return;

    try {
      const stashed = sessionStorage.getItem('org-onboarding-referral-info-restore');
      if (stashed) {
        const parsed = JSON.parse(stashed) as OrgOnboardingReferralInfo;
        setReferralInfo(parsed);
        sessionStorage.removeItem('org-onboarding-referral-info-restore');
      }
    } catch {
      // Ignore parse/storage errors — the ?ref= URL param flow will recover.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Referral codes don't apply to authenticated users — `/getting-started`
  // is a sign-up page. Strip `?ref=` and any cached referral info up-front
  // before any downstream logic reads them.
  React.useEffect(() => {
    if (!router.isReady || !isInitialized) return;
    if (wasAuthenticatedOnMount.current !== true) return;

    if (router.query.ref) {
      const { ref, ...restQuery } = router.query;
      router.replace({ pathname: router.pathname, query: restQuery }, undefined, { shallow: true });
    }
    setReferralInfo(null);
  }, [router.isReady, router, isInitialized, setReferralInfo]);

  // Whether the user is legitimately returning from the Flow 3 login redirect:
  // a logged-in user fills the onboarding form, hits an email/phone collision
  // with no phone-on-file at the last step, gets sent to /login, comes back
  // here to auto-create the entities under their existing account.
  //
  // A true "Flow 3 return" must have both atoms populated. If the flag is set
  // but the data isn't there, it's a stale localStorage value from an
  // abandoned past flow — drop it and redirect.
  const isFlow3Return = isExistingUserFlow && !!restaurantData && !!jpData;

  React.useEffect(() => {
    if (isInitialized && wasAuthenticatedOnMount.current === true && !isFlow3Return) {
      if (isExistingUserFlow) setExistingUserFlow(false);
      router.replace('/employer/homepage');
    }
  }, [isInitialized, router, isFlow3Return, isExistingUserFlow, setExistingUserFlow]);

  // Resolve referral code from ?ref= URL param AFTER auth check.
  React.useEffect(() => {
    if (!router.isReady || !isInitialized) return;

    // Skip referral lookup for authenticated users. They'll either be
    // redirected by the effect above, or they're a Flow 3 return — either
    // way the `?ref=` was stripped upfront and no referral context applies.
    if (wasAuthenticatedOnMount.current === true) {
      setIsReferralLoading(false);
      return;
    }

    const refCode = router.query.ref as string | undefined;
    if (!refCode) {
      setIsReferralLoading(false);
      return;
    }

    orgService
      .lookupReferralCode(refCode)
      .then((response) => {
        const data = response.data;
        const info: OrgOnboardingReferralInfo = {
          referral_code: data.referral_code,
          referrer_restaurant_name: data.referrer_restaurant_name,
          referrer_organization_id: data.referrer_organization_id,
          credits_invitee: data.credits_invitee,
        };
        setReferralInfo(info);

        // The venue this prospect named when they asked for the invite, so the first step
        // opens with the search box already filled. A referrer with more than one open
        // request gets nothing back, which is correct — guessing between them would put one
        // prospect's venue in front of another.
        //
        // Returned into the chain on purpose, so the loading gate below waits for it. The
        // Google autocomplete copies its starting value into internal state once, on mount
        // (`google-auto-complete-input.tsx`), and the onboarding only mounts once this flag
        // clears. Letting this resolve afterwards would fill the box with text that never
        // opens a dropdown — worse than not filling it at all.
        //
        // The inner catch is load-bearing: without it a failure here would reject into the
        // outer handler and wrongly mark a perfectly good referral code invalid.
        return orgService
          .getReferralOnboardingPrefill({ referral_code: data.referral_code })
          .then(({ data: prefill }) => {
            if (prefill?.venue) setReferralPrefill({ venue: prefill.venue });
          })
          .catch(() => {
            // A missing hint is not a failure worth surfacing — the user simply searches.
          });
      })
      .catch(() => {
        setReferralInfo(null);
        setIsInvalidReferralCode(true);
      })
      .finally(() => {
        setIsReferralLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady, router.query.ref, isInitialized]);

  // Resolve the partner code from ?partner= URL param AFTER auth check. The code
  // can be a B2B ambassador or a static acquisition campaign. Mirrors the ?ref=
  // flow but is purely cosmetic: the intro screen needs the name and free days.
  // The partner_code itself is captured by useUTMSource and forwarded to the
  // create-org payload independently, so a failed lookup (an unknown code) still
  // keeps the code as plain attribution and simply shows no intro screen: no
  // "invalid code" branch.
  React.useEffect(() => {
    if (!router.isReady || !isInitialized) return;

    if (wasAuthenticatedOnMount.current === true) {
      setPartnerInfo(null);
      setIsPartnerLoading(false);
      return;
    }

    const partnerCode = router.query.partner as string | undefined;
    if (!partnerCode) {
      setIsPartnerLoading(false);
      return;
    }

    orgService
      .lookupPartnerCode(partnerCode)
      .then((response) => {
        setPartnerInfo({
          partner_code: response.data.partner_code,
          source_type: response.data.source_type,
          free_days: response.data.free_days,
          partner_name: response.data.partner_name,
          campaign_name: response.data.campaign_name,
        });
      })
      .catch(() => {
        setPartnerInfo(null);
      })
      .finally(() => {
        setIsPartnerLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady, router.query.partner, isInitialized]);

  const handleProceedWithout = React.useCallback(() => {
    setIsInvalidReferralCode(false);
    setReferralInfo(null);
    const { ref, ...restQuery } = router.query;
    router.replace({ pathname: router.pathname, query: restQuery }, undefined, { shallow: true });
  }, [router, setReferralInfo]);

  const ogHead = (
    <Head>
      <title>{og.title}</title>
      {/* key="canonical" replaces the default tag from <CanonicalUrl /> in _app instead of adding a second one. */}
      <link rel="canonical" href={og.canonicalUrl} key="canonical" />
      <meta name="description" content={og.description} />
      <meta property="og:title" content={og.title} />
      <meta property="og:description" content={og.description} />
      <meta property="og:image" content={og.image} />
      <meta property="og:url" content={og.canonicalUrl} />
      <meta property="og:type" content="website" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={og.title} />
      <meta name="twitter:description" content={og.description} />
      <meta name="twitter:image" content={og.image} />
    </Head>
  );

  if (wasAuthenticatedOnMount.current === true && !isFlow3Return) {
    return ogHead;
  }

  if (isReferralLoading || isPartnerLoading) {
    return ogHead;
  }

  if (isInvalidReferralCode) {
    return (
      <>
        {ogHead}
        <InvalidReferralCode
          onGoBack={() => router.push('/ask-for-referral')}
          onProceedWithout={handleProceedWithout}
        />
      </>
    );
  }

  return (
    <>
      {ogHead}
      <OrganizationOnboarding />
    </>
  );
};
